// store/report/useReportViewStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";

type ViewState = {
  // Layout
  isSidePanelOpen: boolean;
  toggleSidePanel: () => void;
  
  // Cross-component selections (Sidepanel <-> Actions)
  selectedReports: string[];
  toggleReportSelection: (id: string) => void;
  clearSelection: () => void;
  
  // Active Context
  activeGroupId: string;
  setActiveGroupId: (id: string) => void;

  // Group expand/collapse state
  expandedGroups: Record<string, boolean>;
  toggleGroupExpanded: (id: string) => void;
  removeGroupExpanded: (id: string) => void;
  cleanupExpandedGroups: (validGroupIds: string[]) => void;
};

export const useReportViewStore = create<ViewState>()(
  persist(
    (set) => ({
      isSidePanelOpen: true,
      toggleSidePanel: () => set((s) => ({ isSidePanelOpen: !s.isSidePanelOpen })),

      selectedReports: [],
      toggleReportSelection: (id) => set((state) => ({
        selectedReports: state.selectedReports.includes(id)
          ? state.selectedReports.filter((rid) => rid !== id)
          : [...state.selectedReports, id],
      })),
      clearSelection: () => set({ selectedReports: [] }),

      activeGroupId: "",
      setActiveGroupId: (id) => set({ activeGroupId: id }),

      expandedGroups: {},
      toggleGroupExpanded: (id) => set((s) => ({
        expandedGroups: { ...s.expandedGroups, [id]: !s.expandedGroups[id] },
      })),
      removeGroupExpanded: (id) => set((s) => {
        const { [id]: _, ...rest } = s.expandedGroups;
        return { expandedGroups: rest };
      }),
      cleanupExpandedGroups: (validGroupIds) => set((s) => {
        const cleaned: Record<string, boolean> = {};
        for (const id of validGroupIds) {
          if (id in s.expandedGroups) cleaned[id] = s.expandedGroups[id];
        }
        return { expandedGroups: cleaned };
      }),
    }),
    {
      name: "report-view-store",
      partialize: (state) => ({
        isSidePanelOpen: state.isSidePanelOpen,
        expandedGroups: state.expandedGroups,
      }),
    }
  )
);
